<template>
  <BaseBlock title="Demandes en cours d'étude">
    <v-container>
      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>
            Filtres de recherche
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row align="center">
              <v-col cols="2">
                <v-text-field
                  v-model="search.etablissement"
                  label="établissement"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model="search.demandeur"
                  label="demandeur"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="search.avisRPO"
                  :items="getAvisRPOSelectData()"
                  label="avis du RPO"
                  clearable
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            <SearchDateWidget
              :searchOnDate="search.searchOnDate"
              v-on:update:searchOnDate="search.searchOnDate = $event"
              :dateMin="search.dateMin"
              v-on:update:dateMin="search.dateMin = $event"
              :dateMax="search.dateMax"
              v-on:update:dateMax="search.dateMax = $event"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-data-table
        :headers="headers"
        :items="dossiersEnAttenteDAvis"
        v-if="dossiers"
      >
        <!-- template du header "voir" -->
        <template v-slot:[`header.dossier`]="{}">
          <v-icon>mdi-eye</v-icon>
        </template>
        <!-- template des cellules "voir" -->
        <template v-slot:[`item.dossier`]="{ item }">
          <LinkToPDFWidget :dossier="item" />
        </template>
        <!-- template des cellules "établissement" -->
        <template v-slot:[`item.officine.denomination`]="{ item }">
          <div class="text-body-2">{{ item.officine.denomination }}</div>
          <div class="text-caption grey--text font-italic">
            {{ item.officine.codePostal }}
            -
            {{ item.officine.ville }}
          </div>
        </template>
        <!-- template des cellules "demandeur" -->
        <template v-slot:[`item.demandeur`]="{ item }">
          <div class="text-body-2">{{ item.demandeur }}</div>
          <div class="text-caption grey--text font-italic">
            <a
              target="_blank"
              :href="
                `mailto:${item.contactMail}?subject=Pharmagrément - Suivi du dossier de demande d'agrément de: ${item.officine.denomination}`
              "
            >
              {{ item.contactMail }}
            </a>
          </div>
        </template>
        <!-- template des cellules "type" -->
        <template v-slot:[`item.premiereDemande`]="{ item }">
          <div class="text-body-2">
            {{ item.premiereDemande ? "Première demande" : "Renouvellement" }}
          </div>
        </template>
        <!-- template des cellules "date" -->
        <template v-slot:[`item.dateDepot`]="{ item }">
          <div class="text-body-2">{{ getDate(item.dateDepot) }}</div>
        </template>
        <!-- template des cellules "visite" -->
        <template v-slot:[`item.avisRPO.visite`]="{ item }">
          <div
            class="text-body-2"
            v-if="
              item.avisRPO && item.avisRPO.visite && item.avisRPO.visite.dateCR
            "
          >
            Visite effectuée
          </div>
          <div
            class="text-body-2"
            v-else-if="
              item.avisRPO &&
                item.avisRPO.visite &&
                item.avisRPO.visite.dateLimite
            "
          >
            Visite à réaliser avant le
            {{ getDate(item.avisRPO.visite.dateLimite) }}
          </div>
          <div class="text-body-2" v-else>
            Pas de visite à programmer
          </div>
        </template>
        <!-- template des cellules "avisRPO" -->
        <template v-slot:[`item.avisRPO`]="{ item }">
          <div
            class="text-body-2"
            v-if="item.avisRPO && null !== item.avisRPO.avis"
          >
            {{ getAvisLibelle(item.avisRPO.avis) }}
          </div>
          <div v-else>
            Pas d'avis
            <span v-if="item.avisRPO && item.avisRPO.enAttente"
              ><v-icon x-small color="red">mdi-star</v-icon></span
            >
          </div>
        </template>
        <!-- template des cellules "actions" -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="text-center">
            <div v-if="item.avisRPO && null !== item.avisRPO.avis">
              <v-btn @click="openEditAvisDialog(item)" x-small>
                modifier un avis
              </v-btn>
            </div>
            <div v-else>
              <v-btn @click="openEditAvisDialog(item)" x-small>
                émettre un avis
              </v-btn>
            </div>
            <div v-if="!(item.avisRPO && null !== item.avisRPO.avis)">
              <v-btn @click="openEditVisiteDialog(item)" x-small>
                visite de l'officine
              </v-btn>
            </div>
          </div>
        </template>
      </v-data-table>
    </v-container>
    <!-- popup voir avis du RPO -->
    <ModalEditAvisRPO
      :avis="currentAvisRPO"
      :dossier="currentDossier"
      :display="modalEditAvisRPODisplay"
      v-on:update:display="modalEditAvisRPODisplay = $event"
      v-on:ModalAvisRPOAction="actionAvisRPO($event)"
      v-if="modalEditAvisRPODisplay"
    />
    <!-- popup voir visite -->
    <ModalEditVisite
      :avis="currentAvisRPO"
      :dossier="currentDossier"
      :display="modalEditVisiteDisplay"
      v-on:update:display="modalEditVisiteDisplay = $event"
      v-on:ModalAvisRPOAction="actionVisiteRPO($event)"
      v-if="modalEditVisiteDisplay"
    />
  </BaseBlock>
</template>

<script>
import { mapState } from "vuex";
import common from "@/common";
import ApiAvisRPORepository from "@/repositories/ApiAvisRPORepository";
import BaseBlock from "@/components/base/BaseBlock";
import LinkToPDFWidget from "@/components/LinkToPDFWidget";
import SearchDateWidget from "@/components/SearchDateWidget";
import ModalEditAvisRPO from "@/components/ModalEditAvisRPO";
import ModalEditVisite from "@/components/ModalEditVisite";

export default {
  name: "RecevabiliteDesDemandes",
  components: {
    BaseBlock,
    LinkToPDFWidget,
    ModalEditAvisRPO,
    ModalEditVisite,
    SearchDateWidget
  },
  data() {
    return {
      refresh_dossier_timer: null,
      modalEditAvisRPODisplay: false,
      modalEditVisiteDisplay: false,
      currentAvisRPO: null,
      currentDossier: null,
      search: {
        etablissement: "",
        demandeur: "",
        avisRPO: null,
        searchOnDate: false,
        dateMin: null,
        dateMax: null
      },
      headers: [
        {
          align: "center",
          sortable: false,
          value: "dossier"
        },
        {
          text: "Etablissement",
          align: "start",
          sortable: true,
          value: "officine.denomination"
        },
        {
          text: "Demandeur",
          align: "start",
          sortable: true,
          value: "demandeur"
        },
        {
          text: "Type",
          align: "start",
          sortable: true,
          value: "premiereDemande"
        },
        {
          text: "Date de dépot de la demande",
          align: "start",
          sortable: true,
          value: "dateDepot"
        },
        {
          text: "Visite",
          align: "start",
          sortable: false,
          value: "avisRPO.visite"
        },
        {
          text: "Avis du RPO",
          align: "start",
          sortable: false,
          value: "avisRPO"
        },
        {
          text: "Actions",
          align: "center",
          sortable: false,
          value: "actions"
        }
      ]
    };
  },
  computed: {
    ...mapState(["dossiers", "user"]),
    dossiersEnAttenteDAvis() {
      return this.dossiers
        .filter(d => {
          if ("" !== this.search.etablissement) {
            if (
              !d.officine ||
              !d.officine.denomination
                .toLowerCase()
                .includes(this.search.etablissement)
            )
              return false;
          }
          if ("" !== this.search.demandeur) {
            if (!d.contactPrenom || !d.contactNom) return false;
            if (
              !(d.contactPrenom + " " + d.contactNom)
                .toLowerCase()
                .includes(this.search.demandeur)
            )
              return false;
          }
          if (null !== this.search.avisRPO) {
            if (
              this.search.avisRPO === 2 &&
              d.avisRPO &&
              d.avisRPO.avis !== null
            )
              return false;
            if (
              this.search.avisRPO === false &&
              (!d.avisRPO || d.avisRPO.avis === true || d.avisRPO.avis === null)
            )
              return false;
            if (
              this.search.avisRPO === true &&
              (!d.avisRPO ||
                d.avisRPO.avis === false ||
                d.avisRPO.avis === null)
            )
              return false;
          }
          if (
            this.search.searchOnDate &&
            !common.matchDate(d, this.search.dateMin, this.search.dateMax)
          )
            return false;
          return common.isAttente(d);
        })
        .map(d => {
          d.demandeur = d.contactPrenom + " " + d.contactNom;
          return d;
        });
    }
  },
  async created() {
    if (null === this.dossiers) await this.loadDossiers();
    else this.loadDossiers();
    this.refresh_dossier_timer = setInterval(
      this.loadDossiers,
      common.REFRESH_TIMER
    );
  },
  beforeDestroy() {
    clearInterval(this.refresh_dossier_timer);
  },
  methods: {
    async loadDossiers() {
      this.$store.dispatch("loadDossiers");
    },
    getDate(timestamp) {
      return common.formatDate(timestamp);
    },
    getAvisLibelle(avis) {
      return common.getAvisLibelle(avis);
    },
    getAvisRPOSelectData() {
      return common.getAvisRPOSelectData();
    },
    getCRURL(avis_id) {
      return ApiAvisRPORepository.getCRURL(avis_id);
    },
    async openEditAvisDialog(dossier) {
      this.currentAvisRPO = await common.initAvisRPO(
        dossier,
        this.currentAvisRPO
      );
      this.currentDossier = dossier;
      this.modalEditAvisRPODisplay = true;
    },
    async openEditVisiteDialog(dossier) {
      this.currentAvisRPO = await common.initAvisRPO(
        dossier,
        this.currentAvisRPO
      );
      this.currentDossier = dossier;
      this.modalEditVisiteDisplay = true;
    },
    async actionAvisRPO(dialogData) {
      let avis = null;
      dialogData.dossier = { id: this.currentDossier.id };
      if (null === dialogData.id) {
        //Création
        avis = await ApiAvisRPORepository.new(dialogData);
      } else {
        //modification
        avis = await ApiAvisRPORepository.update(dialogData.id, dialogData);
      }
      this.currentDossier.avisRPO = avis;
      this.$store.dispatch("updateDossier", this.currentDossier);
    },
    async actionVisiteRPO(dialogData) {
      if (dialogData.action === "enregistrer") {
        this.actionEnregistrerVisiteRPO(dialogData);
      } else if (dialogData.action === "supprimer") {
        this.actionSupprimerVisiteRPO(dialogData);
      }
    },
    async actionEnregistrerVisiteRPO(dialogData) {
      let avis = null;
      dialogData.dossier = { id: this.currentDossier.id };
      if (null === dialogData.id) {
        //Création
        avis = await ApiAvisRPORepository.new(dialogData);
        if (dialogData.visite && dialogData.visite.fichierCR) {
          //versement du compteRendu
          avis.visite = await ApiAvisRPORepository.uploadCR(
            avis.id,
            dialogData.visite.fichierCR
          );
        }
      } else {
        //modification
        avis = await ApiAvisRPORepository.update(dialogData.id, dialogData);
        if (dialogData.visite && dialogData.visite.fichierCR) {
          //versement du compteRendu
          avis.visite = await ApiAvisRPORepository.uploadCR(
            avis.id,
            dialogData.visite.fichierCR
          );
        }
      }

      this.currentDossier.avisRPO = avis;
      this.$store.dispatch("updateDossier", this.currentDossier);
    },
    async actionSupprimerVisiteRPO(dialogData) {
      dialogData.dossier = { id: this.currentDossier.id };
      dialogData.visite = {
        id: dialogData.visite.id,
        commentaire: null,
        dateCr: null,
        dateLimite: null,
        compteRendu: null,
        cuNom: null,
        cuPrenom: null,
        cuEmail: null,
        cuTel: null,
        coNom: null,
        coPrenom: null,
        coEmail: null,
        coTel: null
      };
      await ApiAvisRPORepository.deleteVisite(dialogData.id);
      this.currentDossier.avisRPO.visite = null;
      this.$store.dispatch("updateDossier", this.currentDossier);
    }
  }
};
</script>
